/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {getAlertTypes, getNotificationTypes} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseNotificationTypesResult = UseDefaultQueryResult & {
  results: GenericObject[];
};

/**
 * The useNotificationTypes hook provides a Query for referencing the Notification Types.
 *
 * ```typescript
 *
 * import {useNotificationTypes} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {notificationTypes} = useNotificationTypes(enabledAlertTypes);
 *
 * ```
 *
 * import {useNotificationTypes} from '@ion/query/src/hooks/';
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns UseNotificationTypesResult containing the query results and the notification types.
 */
export const useNotificationTypes = (
  {enabledAlertTypes = null},
  enabled = true,
  staleTime = getStaleTime(1, false, false)
): UseNotificationTypesResult => {
  const query = useQuery([QueryKey.NOTIFICATION_TYPES], async() => {
    let results: GenericObject[];
    if (enabledAlertTypes) {
      results = await getAlertTypes({name: `${enabledAlertTypes}`});
    } else {
      results = await getNotificationTypes(false, false);
    }
    return results;
  }, {staleTime: staleTime as number, enabled});

  return createQueryResult(query, {
    results : query.data || []
  }) as UseNotificationTypesResult;
};

export default useNotificationTypes;
